import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useS3VideoUrl } from "@/hooks/useS3VideoUrl";
import {
  VIDEO_DESKTOP_CHROME_S3_FILE,
  VIDEO_DESKTOP_EDGE_S3_FILE,
  VIDEO_DESKTOP_SAFARI_S3_FILE,
} from "@/utils/constants";

import s from "./DesktopInstallPage.module.css";

// ── Full-screen escape ────────────────────────────────────────────────────────
function useFullWidth() {
  useEffect(() => {
    document.body.classList.add("page-fullwidth");
    return () => document.body.classList.remove("page-fullwidth");
  }, []);
}

// ── Platform detection ────────────────────────────────────────────────────────
type PlatformId =
  | "ios"
  | "android"
  | "macos-safari"
  | "chrome"
  | "edge"
  | "brave"
  | "other";

function detectPlatform(): PlatformId {
  if (typeof window === "undefined") return "other";
  const ua = navigator.userAgent;
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  if (/android/i.test(ua) && /mobile/i.test(ua)) return "android";
  if ((navigator as any).brave?.isBrave) return "brave";
  if (/Edg\//i.test(ua)) return "edge";
  if (/^((?!chrome|android|crios|fxios).)*safari/i.test(ua))
    return "macos-safari";
  if (/chrome/i.test(ua)) return "chrome";
  return "other";
}

const PLATFORM_STEPS: Record<PlatformId, string[]> = {
  ios: [
    "Tap the Share icon at the bottom if Safari or right end of address bar if Chrome — the box with an arrow pointing up.",
    'Scroll down and tap "Add to Home Screen".',
    'Tap "Add" in the top-right corner.',
  ],
  android: [
    "Open Maatra in Chrome.",
    "Tap the three-dot menu at the top-right.",
    'Tap "Add to Home Screen" or "Install App".',
    'Tap "Add" to confirm.',
  ],
  "macos-safari": [
    'Click on the Share Icon and click "Add to Dock...".',
    'Click "Add" in the dialog.',
    "Maatra will appear in your Dock.",
  ],
  chrome: [
    "Look for the install icon in the right side of the address bar.",
    'Click it, then click "Install" in the popup.',
    "Maatra opens as a standalone app window.",
    "Pin it to your taskbar or Dock to keep it handy.",
  ],
  edge: [
    "Look for the install icon in the right side of the address bar.",
    'Click it, then click "Install".',
    "Maatra is added to your taskbar and Start menu.",
  ],
  brave: [
    "Open the Brave menu (hamburger icon, top right).",
    'Click "Install Maatra..." from the menu.',
    'Click "Install" to confirm.',
    "Pin it to your taskbar or Dock to keep it handy.",
  ],
  other: [
    "Open Maatra in your browser.",
    'Check the address bar or browser menu for an "Install" or "Add to Home Screen" option.',
    "Follow the prompt to install.",
  ],
};

const PLATFORM_VIDEOS: Partial<Record<PlatformId, string>> = {
  chrome: VIDEO_DESKTOP_CHROME_S3_FILE,
  edge: VIDEO_DESKTOP_EDGE_S3_FILE,
  "macos-safari": VIDEO_DESKTOP_SAFARI_S3_FILE,
  brave: VIDEO_DESKTOP_CHROME_S3_FILE,
};

// ── Logo SVG (exact from marketing repo — full paths with 'a' counters) ───────
function MaatraLogo({ width = 114 }: { width?: number }) {
  const height = Math.round(width * (23 / 114));
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 114 23"
      fill="none"
    >
      <path
        d="M33.6914 22.1512V4.20996H36.0467L42.5492 17.7037L49.0144 4.20996H51.3932V22.1375H48.9261V9.86456L43.1321 22.1492H41.9486L36.1429 9.86456V22.1492H33.6875L33.6914 22.1512Z"
        fill="#45168C"
      />
      <path
        d="M58.6804 22.1515C57.6834 22.1515 56.8492 21.967 56.176 21.598C55.5028 21.229 54.9944 20.7383 54.649 20.1279C54.3036 19.5175 54.1328 18.8463 54.1328 18.1161C54.1328 17.4351 54.2525 16.8364 54.494 16.3222C54.7354 15.808 55.0926 15.3683 55.5656 15.0072C56.0386 14.646 56.6196 14.3536 57.3105 14.1279C57.9091 13.9532 58.5862 13.8001 59.3419 13.6666C60.0975 13.5332 60.8924 13.4095 61.7285 13.2937C62.5627 13.1779 63.391 13.0621 64.2134 12.9444L63.2673 13.4684C63.283 12.4144 63.0612 11.6313 62.6 11.119C62.1388 10.6087 61.3478 10.3536 60.2271 10.3536C59.5205 10.3536 58.8728 10.5184 58.284 10.8462C57.6932 11.174 57.283 11.7157 57.0514 12.4713L54.6215 11.7235C54.9532 10.5695 55.5872 9.65091 56.5214 8.96984C57.4557 8.28878 58.7001 7.94727 60.2526 7.94727C61.4577 7.94727 62.5058 8.15335 63.3988 8.56356C64.2919 8.97377 64.9454 9.62539 65.3615 10.5125C65.5774 10.9522 65.7109 11.4174 65.76 11.908C65.8091 12.3987 65.8346 12.9247 65.8346 13.49V21.7746H63.5303V18.6971L63.9798 19.0955C63.4224 20.126 62.7158 20.8914 61.8561 21.3939C60.9965 21.8963 59.9385 22.1476 58.6863 22.1476L58.6804 22.1515ZM59.1417 20.0219C59.8816 20.0219 60.5156 19.8904 61.0475 19.6294C61.5794 19.3683 62.0073 19.0327 62.3311 18.6264C62.655 18.2202 62.8669 17.7962 62.967 17.3566C63.1083 16.9581 63.1888 16.5087 63.2104 16.0101C63.232 15.5116 63.2418 15.1132 63.2418 14.8148L64.0897 15.1269C63.2673 15.2506 62.5195 15.3644 61.8463 15.4625C61.1731 15.5626 60.5647 15.6608 60.021 15.755C59.4773 15.8511 58.9925 15.965 58.5686 16.0985C58.2114 16.2221 57.8934 16.3732 57.6147 16.5479C57.336 16.7226 57.1162 16.9346 56.9552 17.1838C56.7943 17.4331 56.7118 17.7354 56.7118 18.0926C56.7118 18.4498 56.7982 18.7638 56.9729 19.0582C57.1476 19.3526 57.4125 19.5882 57.7697 19.7629C58.127 19.9375 58.5843 20.0239 59.1397 20.0239L59.1417 20.0219Z"
        fill="#45168C"
      />
      <path
        d="M72.8757 22.1515C71.8787 22.1515 71.0445 21.967 70.3713 21.598C69.6981 21.229 69.1898 20.7383 68.8443 20.1279C68.4989 19.5175 68.3281 18.8463 68.3281 18.1161C68.3281 17.4351 68.4479 16.8364 68.6893 16.3222C68.9307 15.808 69.2879 15.3683 69.7609 15.0072C70.2339 14.646 70.8149 14.3536 71.5058 14.1279C72.1044 13.9532 72.7815 13.8001 73.5372 13.6666C74.2928 13.5332 75.0877 13.4095 75.9239 13.2937C76.758 13.1779 77.5863 13.0621 78.4087 12.9444L77.4626 13.4684C77.4783 12.4144 77.2565 11.6313 76.7953 11.119C76.3341 10.6087 75.5431 10.3536 74.4224 10.3536C73.7158 10.3536 73.0681 10.5184 72.4793 10.8462C71.8885 11.174 71.4783 11.7157 71.2467 12.4713L68.8168 11.7235C69.1485 10.5695 69.7825 9.65091 70.7168 8.96984C71.651 8.28878 72.8954 7.94727 74.4479 7.94727C75.653 7.94727 76.7011 8.15335 77.5941 8.56356C78.4872 8.97377 79.1408 9.62539 79.5569 10.5125C79.7728 10.9522 79.9062 11.4174 79.9553 11.908C80.0044 12.3987 80.0299 12.9247 80.0299 13.49V21.7746H77.7256V18.6971L78.1751 19.0955C77.6177 20.126 76.9111 20.8914 76.0514 21.3939C75.1918 21.8963 74.1339 22.1476 72.8816 22.1476L72.8757 22.1515ZM73.337 20.0219C74.0769 20.0219 74.7109 19.8904 75.2428 19.6294C75.7747 19.3683 76.2026 19.0327 76.5264 18.6264C76.8503 18.2202 77.0622 17.7962 77.1623 17.3566C77.3036 16.9581 77.3841 16.5087 77.4057 16.0101C77.4273 15.5116 77.4371 15.1132 77.4371 14.8148L78.285 15.1269C77.4626 15.2506 76.7148 15.3644 76.0416 15.4625C75.3684 15.5626 74.76 15.6608 74.2163 15.755C73.6726 15.8511 73.1878 15.965 72.7639 16.0985C72.4067 16.2221 72.0887 16.3732 71.81 16.5479C71.5313 16.7226 71.3115 16.9346 71.1505 17.1838C70.9896 17.4331 70.9071 17.7354 70.9071 18.0926C70.9071 18.4498 70.9935 18.7638 71.1682 19.0582C71.3429 19.3526 71.6078 19.5882 71.965 19.7629C72.3223 19.9375 72.7796 20.0239 73.335 20.0239L73.337 20.0219Z"
        fill="#45168C"
      />
      <path
        d="M81.7656 10.5715V8.47921H90.961V10.5715H81.7656ZM90.961 21.9356C90.1131 22.1025 89.2828 22.1731 88.4683 22.1476C87.6538 22.1221 86.9276 21.9651 86.2877 21.6746C85.6479 21.3841 85.167 20.9268 84.8432 20.3046C84.5527 19.7492 84.3957 19.1819 84.376 18.6049C84.3545 18.0279 84.3446 17.3743 84.3446 16.6422V4.74219H86.961V16.5166C86.961 17.0563 86.9668 17.5254 86.9786 17.9238C86.9904 18.3223 87.0767 18.6598 87.2338 18.9327C87.5321 19.4469 88.009 19.7413 88.6607 19.812C89.3123 19.8826 90.0797 19.8551 90.959 19.7315V21.9376L90.961 21.9356Z"
        fill="#45168C"
      />
      <path
        d="M93.7891 22.1517V8.69526H96.107V11.9593L95.7832 11.5353C95.95 11.1035 96.1659 10.7071 96.4309 10.3459C96.6959 9.98477 97.004 9.6884 97.3534 9.45484C97.6929 9.20557 98.0737 9.01322 98.4937 8.87583C98.9137 8.73844 99.3436 8.65601 99.7832 8.62657C100.223 8.59713 100.647 8.62068 101.055 8.69526V11.1369C100.615 11.0211 100.127 10.9877 99.5909 11.0368C99.055 11.0859 98.5624 11.2566 98.1149 11.5471C97.6909 11.8219 97.3573 12.1536 97.1119 12.5442C96.8666 12.9347 96.69 13.3685 96.582 13.8454C96.4741 14.3224 96.4191 14.8268 96.4191 15.3587V22.1478H93.791L93.7891 22.1517Z"
        fill="#45168C"
      />
      <path
        d="M106.844 22.1515C105.847 22.1515 105.013 21.967 104.34 21.598C103.667 21.229 103.159 20.7383 102.813 20.1279C102.468 19.5175 102.297 18.8463 102.297 18.1161C102.297 17.4351 102.417 16.8364 102.658 16.3222C102.899 15.808 103.257 15.3683 103.73 15.0072C104.203 14.646 104.784 14.3536 105.475 14.1279C106.073 13.9532 106.75 13.8001 107.506 13.6666C108.262 13.5332 109.056 13.4095 109.893 13.2937C110.727 13.1779 111.555 13.0621 112.377 12.9444L111.431 13.4684C111.447 12.4144 111.225 11.6313 110.764 11.119C110.303 10.6087 109.512 10.3536 108.391 10.3536C107.685 10.3536 107.037 10.5184 106.448 10.8462C105.857 11.174 105.447 11.7157 105.215 12.4713L102.786 11.7235C103.117 10.5695 103.751 9.65091 104.686 8.96984C105.62 8.28878 106.864 7.94727 108.417 7.94727C109.622 7.94727 110.67 8.15335 111.563 8.56356C112.456 8.97377 113.11 9.62539 113.526 10.5125C113.742 10.9522 113.875 11.4174 113.924 11.908C113.973 12.3987 113.999 12.9247 113.999 13.49V21.7746H111.694V18.6971L112.144 19.0955C111.586 20.126 110.88 20.8914 110.02 21.3939C109.161 21.8963 108.103 22.1476 106.85 22.1476L106.844 22.1515ZM107.306 20.0219C108.046 20.0219 108.68 19.8904 109.212 19.6294C109.743 19.3683 110.171 19.0327 110.495 18.6264C110.819 18.2202 111.031 17.7962 111.131 17.3566C111.272 16.9581 111.353 16.5087 111.374 16.0101C111.396 15.5116 111.406 15.1132 111.406 14.8148L112.254 15.1269C111.431 15.2506 110.684 15.3644 110.01 15.4625C109.337 15.5626 108.729 15.6608 108.185 15.755C107.641 15.8511 107.157 15.965 106.733 16.0985C106.375 16.2221 106.057 16.3732 105.779 16.5479C105.5 16.7226 105.28 16.9346 105.119 17.1838C104.958 17.4331 104.876 17.7354 104.876 18.0926C104.876 18.4498 104.962 18.7638 105.137 19.0582C105.312 19.3526 105.577 19.5882 105.934 19.7629C106.291 19.9375 106.748 20.0239 107.304 20.0239L107.306 20.0219Z"
        fill="#45168C"
      />
      <path
        d="M26.6981 14.2627L20.2074 5.25969L20.0366 5.0222C19.5695 4.3745 18.6039 4.37058 18.1328 5.01827L17.962 5.2538L17.966 5.25773L14.9493 9.42262L16.068 10.9751L19.0847 6.81024L25.2241 15.3245C26.2428 16.7377 25.9209 18.7161 24.5058 19.7348L24.1996 19.9546C22.7845 20.9732 20.8041 20.6514 19.7854 19.2382L16.7903 15.0831L10.7294 6.67874L9.69899 5.24791L9.5302 5.01435C9.06111 4.36469 8.09348 4.36273 7.62439 5.01435L7.4556 5.24791L0.94525 14.2352C-0.666145 16.4609 -0.167613 19.5719 2.06204 21.1833L2.36822 21.405C4.59591 23.0164 7.71075 22.5159 9.32215 20.2902L12.7 15.6268L11.5812 14.0743L7.85011 19.2264C6.82556 20.6396 4.8491 20.9575 3.43398 19.935L3.1278 19.7132C1.71267 18.6906 1.39668 16.7161 2.41925 15.3029L8.57631 6.80043L12.7039 12.5237L13.8227 14.0762L18.3095 20.3C19.915 22.5258 23.0337 23.0321 25.2614 21.4286L25.5676 21.2088C26.0112 20.8888 26.3861 20.51 26.6903 20.092C27.915 18.4021 27.9837 16.0468 26.6981 14.2647V14.2627Z"
        fill="#45168C"
      />
      <path
        d="M13.8188 5.77825C15.4144 5.77825 16.7079 4.48475 16.7079 2.88913C16.7079 1.29351 15.4144 0 13.8188 0C12.2232 0 10.9297 1.29351 10.9297 2.88913C10.9297 4.48475 12.2232 5.77825 13.8188 5.77825Z"
        fill="#45168C"
      />
    </svg>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar({ onInstallClick }: { onInstallClick: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <style>{`
        .ip-nav {
          position:fixed; top:0; left:0; right:0; z-index:200; height:73px; box-sizing:border-box;
          display:flex; justify-content:space-between; align-items:center; padding:20px 64px;
          background:rgba(251,247,255,0.95); backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px);
          border-bottom:1px solid rgba(60,9,108,0.07); font-family:Manrope,sans-serif;
          transition:box-shadow 0.4s;
        }
        .ip-nav.scrolled { box-shadow:0 2px 12px rgba(60,9,108,0.08); }
        .ip-nav-logo { display:flex; align-items:center; text-decoration:none; }
        .ip-nav-links { display:flex; gap:32px; align-items:center; list-style:none; margin:0; padding:0; }
        .ip-nav-links a { font-size:14px; font-weight:500; color:rgba(60,9,108,0.62); text-decoration:none; transition:color 0.2s; }
        .ip-nav-links a:hover { color:#3c096c; }
        .ip-nav-cta { color:#fff; padding:9px 22px; border-radius:100px; font-weight:600; font-size:13px; background:#4C189A; cursor:pointer; border:none; font-family:Manrope,sans-serif; transition:transform 0.18s,box-shadow 0.18s; }
        .ip-nav-cta:hover { transform:translateY(-1px); box-shadow:0 8px 28px rgba(157,78,221,0.5); }
        .ip-hamburger { display:none; background:none; border:none; cursor:pointer; padding:8px; width:40px; height:40px; position:relative; align-items:center; justify-content:center; }
        .ip-hamburger span { width:24px; height:2px; background:#3c096c; border-radius:2px; transition:all 0.3s; position:absolute; }
        .ip-hamburger span:nth-child(1) { top:12px; }
        .ip-hamburger span:nth-child(2) { top:19px; }
        .ip-hamburger span:nth-child(3) { top:26px; }
        .ip-hamburger.open span:nth-child(1) { top:19px; transform:rotate(45deg); }
        .ip-hamburger.open span:nth-child(2) { opacity:0; }
        .ip-hamburger.open span:nth-child(3) { top:19px; transform:rotate(-45deg); }
        .ip-mobile-menu { display:none; position:fixed; top:73px; left:0; right:0; background:rgba(251,247,255,0.98); backdrop-filter:blur(20px); border-bottom:1px solid rgba(60,9,108,0.07); padding:24px 32px; z-index:199; font-family:Manrope,sans-serif; }
        .ip-mobile-menu.open { display:block; }
        .ip-mobile-menu ul { list-style:none; display:flex; flex-direction:column; gap:20px; margin:0; padding:0; }
        .ip-mobile-menu a { font-size:16px; font-weight:500; color:rgba(60,9,108,0.55); text-decoration:none; display:block; padding:8px 0; }
        @media(max-width:768px) {
          .ip-nav { padding:16px 24px; }
          .ip-nav-links { display:none; }
          .ip-hamburger { display:flex; }
        }
      `}</style>
      <nav className={`ip-nav${scrolled ? " scrolled" : ""}`}>
        <a href="https://maatra.health/" className="ip-nav-logo">
          <MaatraLogo />
        </a>
        <ul className="ip-nav-links">
          <li>
            <a href="https://maatra.health/">Home</a>
          </li>
          <li>
            <a href="https://maatra.health/how-it-works">How it works</a>
          </li>
          <li>
            <a href="https://maatra.health/data_privacy">Privacy</a>
          </li>
          <li>
            <a href="https://maatra.health/pricing">Pricing</a>
          </li>
          <li>
            <button className="ip-nav-cta" onClick={onInstallClick}>
              Download app
            </button>
          </li>
        </ul>
        <button
          className={`ip-hamburger${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>
      </nav>
      <div className={`ip-mobile-menu${menuOpen ? " open" : ""}`}>
        <ul>
          <li>
            <a href="https://maatra.health/" onClick={() => setMenuOpen(false)}>
              Home
            </a>
          </li>
          <li>
            <a
              href="https://maatra.health/how-it-works"
              onClick={() => setMenuOpen(false)}
            >
              How it works
            </a>
          </li>
          <li>
            <a
              href="https://maatra.health/data_privacy"
              onClick={() => setMenuOpen(false)}
            >
              Privacy
            </a>
          </li>
          <li>
            <a
              href="https://maatra.health/pricing"
              onClick={() => setMenuOpen(false)}
            >
              Pricing
            </a>
          </li>
          <li>
            <a
              href="https://maatra.health/about-us"
              onClick={() => setMenuOpen(false)}
            >
              About us
            </a>
          </li>
        </ul>
      </div>
    </>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer({ onInstallClick }: { onInstallClick: () => void }) {
  return (
    <>
      <style>{`
        .ip-footer { background:#fff; padding:65px 30px 40px; font-family:Manrope,sans-serif; }
        .ip-footer-inner { max-width:1220px; margin:0 auto; display:flex; flex-direction:column; gap:40px; }
        .ip-footer-top { display:flex; justify-content:space-between; align-items:flex-start; gap:40px; }
        .ip-footer-brand { display:flex; flex-direction:column; gap:32px; max-width:354px; }
        .ip-footer-tagline { font-size:16px; color:#505561; line-height:27.2px; margin:0; }
        .ip-footer-social { display:flex; gap:24px; align-items:center; }
        .ip-footer-social a { display:flex; opacity:0.65; transition:opacity 0.18s; }
        .ip-footer-social a:hover { opacity:1; }
        .ip-footer-menus { display:flex; gap:10px; }
        .ip-footer-col { display:flex; flex-direction:column; gap:36px; width:248px; }
        .ip-footer-col-title { font-size:16px; font-weight:700; color:#0a0a0a; margin:0; }
        .ip-footer-col ul { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:16px; }
        .ip-footer-col a { font-size:16px; color:#505561; text-decoration:none; transition:color 0.18s; }
        .ip-footer-col a:hover { color:#3c096c; }
        .ip-footer-apps { display:flex; flex-direction:column; gap:14px; align-items:flex-start; }
        .ip-footer-badge { display:flex; align-items:center; justify-content:center; background:#0a0a0a; border-radius:100px; padding:16px 32px; text-decoration:none; transition:box-shadow 0.18s,transform 0.18s; }
        .ip-footer-badge:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(0,0,0,0.3); }
        .ip-footer-desktop-btn { background:#4C189A; color:#fff; border:none; border-radius:100px; height:48px; padding:0 32px; font-family:Manrope,sans-serif; font-size:16px; font-weight:500; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; white-space:nowrap; transition:box-shadow 0.18s,transform 0.18s; }
        .ip-footer-desktop-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(76,24,154,0.35); }
        .ip-footer-divider { height:1px; background:rgba(60,9,108,0.08); }
        .ip-footer-copy { font-size:16px; color:#505561; margin:0; }
        @media(max-width:768px) {
          .ip-footer { padding:48px 24px 32px; }
          .ip-footer-top { flex-direction:column; gap:36px; }
          .ip-footer-menus { flex-wrap:wrap; gap:32px; }
          .ip-footer-col { width:auto; }
        }
      `}</style>
      <footer className="ip-footer">
        <div className="ip-footer-inner">
          <div className="ip-footer-top">
            <div className="ip-footer-brand">
              <MaatraLogo />
              <p className="ip-footer-tagline">Family care, made calmer</p>
              <div className="ip-footer-social">
                <a
                  href="https://www.facebook.com/maatra.health/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.99756 16.666C10.8799 16.6768 11.7556 16.5124 12.5739 16.1822C13.3922 15.852 14.1368 15.3627 14.7645 14.7426C15.3923 14.1225 15.8907 13.3839 16.2309 12.5697C16.571 11.7555 16.7462 10.8819 16.7462 9.99952C16.7462 9.11712 16.571 8.24351 16.2309 7.42932C15.8907 6.61513 15.3923 5.87657 14.7645 5.25645C14.1368 4.63634 13.3922 4.14701 12.5739 3.81684C11.7556 3.48666 10.8799 3.32221 9.99756 3.33302C8.24364 3.3545 6.56884 4.06631 5.33615 5.31419C4.10347 6.56207 3.41221 8.24546 3.41221 9.99952C3.41221 11.7536 4.10347 13.437 5.33615 14.6848C6.56884 15.9327 8.24364 16.6445 9.99756 16.666ZM9.99756 18.333C12.2077 18.333 14.3274 17.455 15.8902 15.8922C17.4531 14.3294 18.3311 12.2097 18.3311 9.99952C18.3311 7.78933 17.4531 5.66968 15.8902 4.10684C14.3274 2.54401 12.2077 1.66602 9.99756 1.66602C7.78738 1.66602 5.66772 2.54401 4.10489 4.10684C2.54205 5.66968 1.66406 7.78933 1.66406 9.99952C1.66406 12.2097 2.54205 14.3294 4.10489 15.8922C5.66772 17.455 7.78738 18.333 9.99756 18.333Z"
                      fill="#0A0A0A"
                    />
                    <path
                      d="M9.99756 8.33303C9.99756 7.87303 10.3706 7.49903 10.8316 7.49903H11.6646C11.8856 7.49903 12.0976 7.41122 12.2539 7.2549C12.4102 7.09859 12.4981 6.88659 12.4981 6.66553C12.4981 6.44447 12.4102 6.23247 12.2539 6.07616C12.0976 5.91985 11.8856 5.83203 11.6646 5.83203H10.8316C10.1685 5.83203 9.53264 6.09542 9.0638 6.56426C8.59495 7.03311 8.33156 7.66899 8.33156 8.33203V9.99903H7.49756C7.2765 9.99903 7.0645 10.0868 6.90819 10.2432C6.75188 10.3995 6.66406 10.6115 6.66406 10.8325C6.66406 11.0536 6.75188 11.2656 6.90819 11.4219C7.0645 11.5782 7.2765 11.666 7.49756 11.666H8.33156V16.666C8.33156 16.887 8.41932 17.0988 8.57554 17.2551C8.73176 17.4113 8.94364 17.499 9.16456 17.499C9.38549 17.499 9.59736 17.4113 9.75358 17.2551C9.9098 17.0988 9.99756 16.887 9.99756 16.666V11.666H11.6646C11.8856 11.666 12.0976 11.5782 12.2539 11.4219C12.4102 11.2656 12.4981 11.0536 12.4981 10.8325C12.4981 10.6115 12.4102 10.3995 12.2539 10.2432C12.0976 10.0868 11.8856 9.99903 11.6646 9.99903H9.99756V8.33303Z"
                      fill="#0A0A0A"
                    />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/maatra.health/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13.3311 3.33302H6.66406C5.7801 3.33302 4.93233 3.68417 4.30728 4.30923C3.68222 4.93429 3.33106 5.78205 3.33106 6.66602V13.333C3.33106 14.217 3.68222 15.0647 4.30728 15.6898C4.93233 16.3149 5.7801 16.666 6.66406 16.666H13.3311C14.215 16.666 15.0628 16.3149 15.6878 15.6898C16.3129 15.0647 16.6641 14.217 16.6641 13.333V6.66602C16.6641 5.78205 16.3129 4.93429 15.6878 4.30923C15.0628 3.68417 14.215 3.33302 13.3311 3.33302ZM6.66406 1.66602C6.00745 1.66602 5.35727 1.79534 4.75065 2.04662C4.14402 2.29789 3.59282 2.66619 3.12853 3.13048C2.19085 4.06816 1.66406 5.33993 1.66406 6.66602V13.333C1.66406 14.6591 2.19085 15.9309 3.12853 16.8685C4.06621 17.8062 5.33798 18.333 6.66406 18.333H13.3311C14.6571 18.333 15.9289 17.8062 16.8666 16.8685C17.8043 15.9309 18.3311 14.6591 18.3311 13.333V6.66602C18.3311 5.33993 17.8043 4.06816 16.8666 3.13048C15.9289 2.1928 14.6571 1.66602 13.3311 1.66602H6.66406Z"
                      fill="#0A0A0A"
                    />
                    <path
                      d="M14.1694 6.667C14.3905 6.667 14.6025 6.57919 14.7588 6.42287C14.9151 6.26656 15.0029 6.05456 15.0029 5.8335C15.0029 5.61244 14.9151 5.40044 14.7588 5.24413C14.6025 5.08781 14.3905 5 14.1694 5C13.9484 5 13.7364 5.08781 13.5801 5.24413C13.4238 5.40044 13.3359 5.61244 13.3359 5.8335C13.3359 6.05456 13.4238 6.26656 13.5801 6.42287C13.7364 6.57919 13.9484 6.667 14.1694 6.667Z"
                      fill="#0A0A0A"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.1694 10.0009C14.178 10.5535 14.0766 11.1022 13.871 11.6152C13.6655 12.1282 13.3599 12.5952 12.9722 12.989C12.5844 13.3828 12.1222 13.6956 11.6125 13.909C11.1027 14.1225 10.5556 14.2324 10.0029 14.2324C9.45029 14.2324 8.90316 14.1225 8.3934 13.909C7.88364 13.6956 7.42143 13.3828 7.03369 12.989C6.64595 12.5952 6.34041 12.1282 6.13486 11.6152C5.92932 11.1022 5.82788 10.5535 5.83644 10.0009C5.85338 8.90704 6.29981 7.86373 7.07933 7.09619C7.85886 6.32865 8.90896 5.89844 10.0029 5.89844C11.0969 5.89844 12.147 6.32865 12.9265 7.09619C13.7061 7.86373 14.1525 8.90704 14.1694 10.0009ZM12.5034 10.0009C12.5034 10.6639 12.24 11.2998 11.7712 11.7687C11.3024 12.2375 10.6665 12.5009 10.0034 12.5009C9.3404 12.5009 8.70451 12.2375 8.23567 11.7687C7.76683 11.2998 7.50344 10.6639 7.50344 10.0009C7.50344 9.33785 7.76683 8.70196 8.23567 8.23312C8.70451 7.76428 9.3404 7.50089 10.0034 7.50089C10.6665 7.50089 11.3024 7.76428 11.7712 8.23312C12.24 8.70196 12.5034 9.33785 12.5034 10.0009Z"
                      fill="#0A0A0A"
                    />
                  </svg>
                </a>
              </div>
            </div>

            <div className="ip-footer-menus">
              <div className="ip-footer-col">
                <p className="ip-footer-col-title">Menu</p>
                <ul>
                  <li>
                    <a href="https://maatra.health/">Home</a>
                  </li>
                  <li>
                    <a href="https://maatra.health/how-it-works">
                      How it works
                    </a>
                  </li>
                  <li>
                    <a href="https://maatra.health/data_privacy">Privacy</a>
                  </li>
                  <li>
                    <a href="https://maatra.health/pricing">Pricing</a>
                  </li>
                  <li>
                    <a href="https://maatra.health/faq">FAQ&apos;s</a>
                  </li>
                </ul>
              </div>
              <div className="ip-footer-col">
                <p className="ip-footer-col-title">Company</p>
                <ul>
                  <li>
                    <a href="https://maatra.health/about-us">About Us</a>
                  </li>
                  <li>
                    <a href="https://maatra.health/support">Support</a>
                  </li>
                  <li>
                    <a href="https://maatra.health/privacy-policy">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href="https://maatra.health/terms">Terms of Use</a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="ip-footer-apps">
              {/* App Store badge */}
              <a
                href="https://apps.apple.com/in/app/maatra/id6751178557"
                target="_blank"
                rel="noreferrer"
                className="ip-footer-badge"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="132"
                  height="35"
                  viewBox="0 0 132 35"
                  fill="none"
                >
                  <path
                    d="M20.7844 15.8868C20.7496 12.0231 23.9494 10.1443 24.0953 10.057C22.284 7.41697 19.4753 7.05571 18.4887 7.02693C16.1306 6.7788 13.8429 8.43724 12.642 8.43724C11.4163 8.43724 9.56634 7.05074 7.57244 7.09144C5.00688 7.13114 2.60607 8.61589 1.29004 10.9224C-1.42638 15.6248 0.599272 22.5344 3.20255 26.3356C4.50469 28.1975 6.02616 30.2758 8.01708 30.2024C9.96532 30.123 10.6938 28.9618 13.045 28.9618C15.3743 28.9618 16.0572 30.2024 18.0888 30.1557C20.1789 30.123 21.496 28.2859 22.7524 26.4071C24.257 24.2753 24.8615 22.1722 24.8853 22.065C24.8376 22.0481 20.826 20.5167 20.7844 15.8868ZM16.9494 4.52587C17.9975 3.2158 18.714 1.4333 18.5145 -0.375C16.998 -0.307511 15.1014 0.674053 14.0087 1.95535C13.043 3.0838 12.1795 4.93477 12.4029 6.67559C14.1069 6.80262 15.8577 5.8161 16.9494 4.52587ZM49.0036 29.9344H46.2812L44.7895 25.2489H39.6048L38.1845 29.9334H35.5336L40.6707 13.9822H43.8437L49.0036 29.9344ZM44.3389 23.2828L42.9911 19.1173C42.8482 18.6925 42.5792 17.6901 42.1872 16.1131H42.1376C41.9798 16.791 41.7267 17.7934 41.3803 19.1173L40.0544 23.2828H44.3389ZM62.1897 24.042C62.1897 25.9972 61.6587 27.5435 60.5948 28.6789C59.643 29.6892 58.4599 30.1944 57.0486 30.1944C55.5242 30.1944 54.4285 29.6515 53.7635 28.5628V34.5971H51.2069V22.2139C51.2069 20.9852 51.1751 19.7247 51.1126 18.4325H53.3606L53.5035 20.2557H53.5511C54.4037 18.8821 55.6969 18.1953 57.4337 18.1953C58.7904 18.1953 59.9238 18.7313 60.83 19.8041C61.7351 20.879 62.1897 22.2913 62.1897 24.042ZM59.5854 24.1353C59.5854 23.0158 59.3333 22.0928 58.8271 21.3653C58.2753 20.609 57.533 20.2299 56.602 20.2299C55.9718 20.2299 55.3991 20.4413 54.887 20.8572C54.3741 21.2752 54.0191 21.8557 53.8806 22.5027C53.8112 22.7568 53.7715 23.0178 53.7615 23.2808V25.1993C53.7615 26.0349 54.0196 26.7406 54.5317 27.3182C55.0448 27.8938 55.7118 28.1827 56.5315 28.1827C57.4942 28.1827 58.2436 27.8095 58.7795 27.0711C59.3164 26.3287 59.5854 25.3501 59.5854 24.1353ZM75.4224 24.041C75.4224 25.9982 74.8914 27.5445 73.8275 28.6789C72.8747 29.6892 71.6927 30.1944 70.2804 30.1944C68.7559 30.1944 67.6602 29.6515 66.9953 28.5628V34.5971H64.4386V22.2139C64.4386 20.9852 64.4059 19.7247 64.3444 18.4325H66.5923L66.7342 20.2557H66.7829C67.6344 18.8821 68.9266 18.1953 70.6645 18.1953C72.0212 18.1953 73.1536 18.7313 74.0627 19.8041C74.9659 20.879 75.4224 22.2903 75.4224 24.041ZM72.8172 24.1353C72.8172 23.0158 72.5641 22.0928 72.0579 21.3653C71.5051 20.609 70.7657 20.2299 69.8348 20.2299C69.2045 20.2299 68.6309 20.4413 68.1178 20.8572C67.6037 21.276 67.2692 21.8238 67.1124 22.5027C67.034 22.8193 66.9933 23.0763 66.9933 23.2808V25.1993C66.9933 26.0349 67.2503 26.7406 67.7605 27.3182C68.2736 27.8929 68.9415 28.1827 69.7633 28.1827C70.727 28.1827 71.4763 27.8095 72.0113 27.0711C72.5482 26.3287 72.8172 25.3501 72.8172 24.1353ZM90.2164 25.4593C90.2164 26.818 89.7419 27.9206 88.7971 28.7742C87.76 29.7041 86.3099 30.1696 84.452 30.1696C82.735 30.1696 81.3585 29.8391 80.3203 29.1771L80.9109 27.0463C82.0323 27.7112 83.263 28.0417 84.6039 28.0417C85.5676 28.0417 86.3169 27.8234 86.8518 27.3907C87.3878 26.9569 87.6587 26.3763 87.6587 25.6528C87.6587 25.0037 87.4344 24.4618 86.9938 24.0192C86.5521 23.5775 85.8206 23.1657 84.7934 22.7885C81.9996 21.7474 80.6022 20.2239 80.6022 18.2201C80.6022 16.9101 81.0955 15.8392 82.081 15.0015C83.0665 14.1649 84.3736 13.747 85.9993 13.747C87.4513 13.747 88.6611 13.9991 89.6219 14.5043L88.9797 16.5875C88.0766 16.0982 87.0553 15.8531 85.9149 15.8531C85.0128 15.8531 84.3051 16.0754 83.8 16.517C83.3732 16.911 83.1578 17.3914 83.1578 17.9621C83.1578 18.5913 83.404 19.1134 83.8923 19.5242C84.317 19.9014 85.0922 20.3123 86.2117 20.7539C87.5853 21.3077 88.5956 21.9519 89.2417 22.6932C89.8928 23.4336 90.2164 24.3576 90.2164 25.4593ZM98.6892 20.35H95.8715V25.9347C95.8715 27.3539 96.3677 28.0626 97.3632 28.0626C97.8197 28.0626 98.1989 28.0249 98.4986 27.9435L98.5691 29.8848C98.0659 30.0733 97.4029 30.1676 96.5811 30.1676C95.5718 30.1676 94.7828 29.8599 94.2131 29.2436C93.6464 28.6283 93.3615 27.5961 93.3615 26.1431V20.345H91.6822V18.4276H93.3615V16.3215L95.8725 15.5633V18.4276H98.6892V20.35ZM111.376 24.0887C111.376 25.8563 110.871 27.3073 109.861 28.4427C108.805 29.6118 107.399 30.1944 105.647 30.1944C103.957 30.1944 102.613 29.6347 101.611 28.5161C100.609 27.3946 100.107 25.9833 100.107 24.2802C100.107 22.4977 100.625 21.0378 101.658 19.9034C102.693 18.766 104.085 18.1983 105.837 18.1983C107.525 18.1983 108.881 18.759 109.907 19.8786C110.889 20.9643 111.376 22.3667 111.376 24.0887ZM108.727 24.1472C108.727 23.0932 108.499 22.1901 108.042 21.4338C107.506 20.5207 106.739 20.0642 105.745 20.0642C104.721 20.0642 103.935 20.5207 103.401 21.4338C102.943 22.1901 102.716 23.1091 102.716 24.1949C102.716 25.2499 102.943 26.156 103.401 26.9103C103.953 27.8234 104.724 28.2799 105.723 28.2799C106.7 28.2799 107.467 27.8135 108.02 26.8875C108.491 26.1133 108.727 25.2032 108.727 24.1472ZM119.689 20.6795C119.423 20.6316 119.153 20.608 118.883 20.609C117.984 20.609 117.288 20.9465 116.799 21.6273C116.375 22.2268 116.162 22.984 116.162 23.8991V29.9334H113.605V22.0551C113.607 20.8478 113.582 19.6407 113.531 18.4345H115.757L115.851 20.6348H115.922C116.193 19.8786 116.617 19.2682 117.2 18.8116C117.733 18.4129 118.381 18.197 119.047 18.1963C119.283 18.1963 119.497 18.2122 119.686 18.242L119.689 20.6795ZM131.12 23.6391C131.127 24.0271 131.096 24.4152 131.027 24.7983H123.358C123.387 25.9347 123.759 26.8041 124.471 27.4036C125.118 27.9385 125.954 28.2075 126.98 28.2075C128.115 28.2075 129.15 28.0268 130.082 27.6636L130.482 29.4362C129.395 29.9116 128.109 30.1478 126.626 30.1478C124.842 30.1478 123.442 29.6218 122.422 28.5747C121.405 27.5256 120.896 26.1163 120.896 24.3507C120.896 22.6178 121.369 21.1738 122.318 20.0215C123.311 18.7908 124.652 18.1765 126.341 18.1765C127.998 18.1765 129.254 18.7918 130.106 20.0215C130.781 20.9961 131.12 22.2049 131.12 23.6391ZM128.683 22.9761C128.701 22.2169 128.533 21.5638 128.187 21.011C127.744 20.2994 127.063 19.9451 126.151 19.9451C125.315 19.9451 124.635 20.2914 124.117 20.9872C123.691 21.539 123.438 22.203 123.36 22.9731L128.683 22.9761ZM38.8684 8.34296C38.1577 8.34296 37.5424 8.30822 37.0313 8.24966V0.538083C37.7469 0.426925 38.4704 0.372338 39.1949 0.375316C42.1267 0.375316 43.4764 1.8164 43.4764 4.16659C43.4764 6.87905 41.8815 8.34296 38.8684 8.34296ZM39.2981 1.36383C38.9588 1.35741 38.6197 1.38467 38.2858 1.44521V7.3078C38.4366 7.33162 38.7274 7.34253 39.1353 7.34253C41.0558 7.34253 42.1495 6.24882 42.1495 4.20232C42.1495 2.37616 41.16 1.36383 39.2981 1.36383ZM47.6836 8.4025C46.0311 8.4025 44.9602 7.16885 44.9602 5.49453C44.9602 3.74876 46.0539 2.50518 47.7769 2.50518C49.4045 2.50518 50.4992 3.67929 50.4992 5.40223C50.4992 7.16885 49.3718 8.4025 47.6836 8.4025ZM47.7312 3.42323C46.8241 3.42323 46.2425 4.2718 46.2425 5.45781C46.2425 6.62199 46.836 7.47056 47.7193 7.47056C48.6026 7.47056 49.1961 6.56344 49.1961 5.43399C49.1961 4.28271 48.6145 3.42323 47.7312 3.42323ZM59.9209 2.6213L58.1523 8.27348H57.001L56.2675 5.81908C56.0853 5.21841 55.9336 4.60891 55.813 3.99291H55.7892C55.6969 4.60924 55.5212 5.22656 55.3366 5.81908L54.5575 8.27348H53.3933L51.7279 2.62229H53.0182L53.6593 5.30894C53.8102 5.94909 53.9372 6.55351 54.0424 7.12419H54.0672C54.1595 6.6478 54.3104 6.0533 54.5317 5.32184L55.3356 2.62329H56.3588L57.128 5.26329C57.3136 5.90344 57.4655 6.53168 57.5826 7.12518H57.6153C57.6977 6.54359 57.8257 5.92626 57.9994 5.26428L58.6862 2.62329L59.9199 2.62229L59.9209 2.6213ZM66.4315 8.27348H65.1751V5.02807C65.1751 4.02864 64.791 3.52744 64.0337 3.52744C63.2893 3.52744 62.7782 4.16759 62.7782 4.91195V8.27348H61.5217V4.23706C61.5217 3.73685 61.5098 3.20091 61.4751 2.61932H62.5807L62.6393 3.4927H62.674C63.0115 2.88828 63.6973 2.50518 64.4664 2.50518C65.6534 2.50518 66.4325 3.41231 66.4325 4.88912L66.4315 8.27348ZM69.8963 8.27348H68.6388V0.0269549H69.8963V8.27447V8.27348ZM74.4776 8.4025C72.8261 8.4025 71.7542 7.16885 71.7542 5.49453C71.7542 3.74876 72.8479 2.50518 74.5699 2.50518C76.1995 2.50518 77.2923 3.67929 77.2923 5.40223C77.2933 7.16885 76.1648 8.4025 74.4776 8.4025ZM74.5242 3.42323C73.6161 3.42323 73.0355 4.2718 73.0355 5.45781C73.0355 6.62199 73.63 7.47056 74.5113 7.47056C75.3956 7.47056 75.9881 6.56344 75.9881 5.43399C75.9891 4.28271 75.4095 3.42323 74.5242 3.42323ZM82.2457 8.27348L82.1554 7.62341H82.1217C81.7386 8.14644 81.1788 8.4025 80.4682 8.4025C79.4559 8.4025 78.7363 7.69288 78.7363 6.73811C78.7363 5.34268 79.9462 4.62115 82.0413 4.62115V4.51793C82.0413 3.77357 81.6463 3.40139 80.8662 3.40139C80.3434 3.39224 79.8295 3.53796 79.3894 3.82022L79.1343 2.99447C79.6563 2.66894 80.3094 2.50617 81.0756 2.50617C82.5524 2.50617 83.2988 3.28527 83.2988 4.84446V6.92668C83.2988 7.49736 83.3236 7.93902 83.3792 8.27646L82.2467 8.27447L82.2457 8.27348ZM82.074 5.45781C80.6786 5.45781 79.9779 5.79625 79.9779 6.59817C79.9779 7.19168 80.3392 7.48148 80.8404 7.48148C81.4795 7.48148 82.074 6.99318 82.074 6.3302V5.45781ZM89.3985 8.27348L89.341 7.36635H89.3062C88.945 8.05117 88.3386 8.4025 87.49 8.4025C86.1273 8.4025 85.118 7.20359 85.118 5.51736C85.118 3.74876 86.165 2.5032 87.5942 2.5032C88.3505 2.5032 88.8864 2.75926 89.1881 3.27237H89.2139V0.0269549H90.4714V6.75102C90.4714 7.29787 90.4843 7.80999 90.5181 8.27447H89.3995L89.3985 8.27348ZM89.2139 4.94768C89.2139 4.15667 88.6899 3.48178 87.89 3.48178C86.958 3.48178 86.3893 4.30753 86.3893 5.46972C86.3893 6.61008 86.9799 7.39017 87.8652 7.39017C88.6552 7.39017 89.2139 6.70338 89.2139 5.88954V4.94768ZM98.446 8.40151C96.7945 8.40151 95.7236 7.16885 95.7236 5.49354C95.7236 3.74876 96.8173 2.50518 98.5393 2.50518C100.168 2.50518 101.262 3.67929 101.262 5.40223C101.263 7.16885 100.135 8.40151 98.446 8.40151ZM98.4926 3.42323C97.5855 3.42323 97.0039 4.2718 97.0039 5.45781C97.0039 6.62199 97.5974 7.47056 98.4797 7.47056C99.365 7.47056 99.9576 6.56344 99.9576 5.43399C99.9595 4.28271 99.3779 3.42323 98.4926 3.42323ZM108.019 8.27348H106.761V5.02807C106.761 4.02864 106.378 3.52744 105.622 3.52744C104.876 3.52744 104.366 4.16759 104.366 4.91195V8.27348H103.109V4.23706C103.109 3.73685 103.096 3.20091 103.061 2.61932H104.167L104.225 3.4927H104.26C104.597 2.88828 105.284 2.5032 106.051 2.5032C107.238 2.5032 108.019 3.41132 108.019 4.88813V8.27348ZM116.472 3.56317H115.091V6.30837C115.091 7.00708 115.333 7.35544 115.821 7.35544C116.044 7.35544 116.229 7.33162 116.379 7.29688L116.414 8.25065C116.171 8.34494 115.844 8.39159 115.439 8.39159C114.449 8.39159 113.856 7.84473 113.856 6.41357V3.56317H113.032V2.62031H113.856V1.58614L115.091 1.21396V2.6213H116.472V3.56416V3.56317ZM123.125 8.27348H121.87V5.05189C121.87 4.03956 121.486 3.52843 120.729 3.52843C120.078 3.52843 119.472 3.97108 119.472 4.86629V8.27348H118.216V0.0269549H119.472V3.42323H119.497C119.893 2.80689 120.465 2.5032 121.197 2.5032C122.394 2.5032 123.125 3.43414 123.125 4.91195V8.27348ZM129.939 5.7496H126.169C126.193 6.8195 126.901 7.42392 127.95 7.42392C128.508 7.42392 129.021 7.33062 129.474 7.15595L129.669 8.02933C129.135 8.26157 128.505 8.37769 127.773 8.37769C126.004 8.37769 124.958 7.26115 124.958 5.52927C124.958 3.79541 126.03 2.49228 127.632 2.49228C129.076 2.49228 129.982 3.56317 129.982 5.17992C129.991 5.37084 129.976 5.56211 129.939 5.7496ZM128.787 4.85438C128.787 3.981 128.347 3.36566 127.544 3.36566C126.822 3.36566 126.251 3.9939 126.17 4.85438H128.787Z"
                    fill="white"
                  />
                </svg>
              </a>
              {/* Play Store badge */}
              <a
                href="https://play.google.com/store/apps/details?id=health.maatra.app&pli=1"
                target="_blank"
                rel="noreferrer"
                className="ip-footer-badge"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="129"
                  height="30"
                  viewBox="0 0 129 30"
                  fill="none"
                >
                  <g clipPath="url(#clip0_2288_14397)">
                    <path
                      d="M41.7769 4.19798C41.7769 5.08908 41.5547 5.86903 40.998 6.42572C40.3302 7.09453 39.5492 7.42795 38.547 7.42795C37.5447 7.42795 36.7648 7.09453 36.096 6.42572C35.4282 5.7569 35.0938 4.97695 35.0938 3.97472C35.0938 2.97248 35.4282 2.19253 36.096 1.52372C36.7648 0.855892 37.5447 0.521484 38.547 0.521484C38.9935 0.521484 39.4381 0.632623 39.8836 0.855892C40.3302 1.07817 40.6636 1.30144 40.8858 1.63585L40.3302 2.19253C39.8836 1.63585 39.3279 1.41258 38.547 1.41258C37.8792 1.41258 37.2103 1.63585 36.7648 2.19253C36.2081 2.63907 35.9848 3.30689 35.9848 4.08685C35.9848 4.86581 36.2081 5.53462 36.7648 5.98017C37.3215 6.42572 37.8792 6.76012 38.547 6.76012C39.3269 6.76012 39.8836 6.53685 40.4413 5.98017C40.7757 5.64576 40.998 5.20022 40.998 4.64353H38.547V3.75244H41.7779V4.19798H41.7769ZM46.9022 1.41258H43.8945V3.52917H46.6789V4.30912H43.8945V6.42572H46.9012V7.31681H43.0034V0.632623H46.9022V1.41258ZM50.5777 7.31681H49.6866V1.41258H47.7933V0.632623H52.472V1.41258H50.5777V7.31681ZM55.702 7.31681V0.632623H56.5931V7.31681H55.702ZM60.3807 7.31681H59.4896V1.41258H57.5963V0.632623H62.1629V1.41258H60.2696V7.31681H60.3807ZM70.9637 6.42572C70.2949 7.09453 69.5149 7.42795 68.5127 7.42795C67.5105 7.42795 66.7305 7.09453 66.0617 6.42572C65.3939 5.7569 65.0595 4.97695 65.0595 3.97472C65.0595 2.97248 65.3939 2.19253 66.0617 1.52372C66.7305 0.855892 67.5105 0.521484 68.5127 0.521484C69.5149 0.521484 70.2949 0.855892 70.9637 1.52372C71.6315 2.19253 71.9659 2.97248 71.9659 3.97472C71.9659 4.97695 71.6315 5.7569 70.9637 6.42572ZM66.7305 5.86903C67.177 6.31458 67.8449 6.64799 68.5127 6.64799C69.1815 6.42572 69.8493 6.42572 70.2949 5.86903C70.7948 5.36535 71.0752 4.68437 71.0748 3.97472C71.0748 3.19476 70.8525 2.52595 70.2949 2.08139C69.8493 1.63485 69.1815 1.30144 68.5127 1.30144C67.8449 1.30144 67.1761 1.52471 66.7305 2.08139C66.2308 2.58483 65.9505 3.2654 65.9505 3.97472C65.9505 4.75467 66.1738 5.42348 66.7305 5.86903ZM73.1914 7.31681V0.632623H74.1936L77.4246 5.86903V0.63163H78.3157V7.31581H77.4246L73.9714 1.85812V7.31581H73.1914V7.31681Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="0.992481"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M64.8356 17.1173C62.1614 17.1173 60.0448 19.1217 60.0448 21.9071C60.04 22.5375 60.1607 23.1626 60.3998 23.746C60.6389 24.3293 60.9916 24.8593 61.4375 25.305C61.8833 25.7508 62.4133 26.1034 62.9967 26.3424C63.5801 26.5813 64.2052 26.7019 64.8356 26.697C67.5089 26.697 69.6255 24.6925 69.6255 21.9071C69.6255 19.0106 67.5099 17.1173 64.8356 17.1173ZM64.8356 24.6915C63.3869 24.6915 62.1614 23.466 62.1614 21.796C62.1614 20.1249 63.3869 18.8984 64.8356 18.8984C66.2834 18.8984 67.5089 20.0128 67.5089 21.796C67.5089 23.466 66.2834 24.6915 64.8356 24.6915ZM54.476 17.1173C51.8017 17.1173 49.6851 19.1217 49.6851 21.9071C49.6803 22.5375 49.801 23.1625 50.04 23.7457C50.279 24.329 50.6317 24.8589 51.0774 25.3047C51.5231 25.7504 52.0531 26.103 52.6364 26.3421C53.2196 26.5811 53.8446 26.7017 54.475 26.697C57.1492 26.697 59.2658 24.6925 59.2658 21.9071C59.2658 19.0106 57.1502 17.1173 54.476 17.1173ZM54.476 24.6915C53.0272 24.6915 51.8017 23.466 51.8017 21.796C51.8017 20.1249 53.0272 18.8984 54.475 18.8984C55.9237 18.8984 57.1492 20.0128 57.1492 21.796C57.1492 23.466 55.9247 24.6915 54.476 24.6915ZM42.1098 18.565V20.5695H46.9007C46.7886 21.6839 46.343 22.5749 45.7863 23.1326C45.1185 23.8004 44.0041 24.5814 42.1108 24.5814C39.1031 24.5814 36.8744 22.2415 36.8744 19.2329C36.8744 16.2262 39.2143 13.8863 42.1108 13.8863C43.6697 13.8863 44.8952 14.5551 45.7863 15.3351L47.2351 13.8863C46.0096 12.7719 44.4497 11.8818 42.222 11.8818C38.211 11.8818 34.7578 15.2229 34.7578 19.2338C34.7578 23.2428 38.211 26.5859 42.222 26.5859C44.4497 26.5859 46.0096 25.917 47.3462 24.4693C48.6829 23.1326 49.1284 21.2383 49.1284 19.7905C49.1284 19.344 49.1284 18.8994 49.0173 18.565H42.1098ZM92.6848 20.1249C92.2382 19.0106 91.1249 17.1173 88.6739 17.1173C86.2229 17.1173 84.2184 19.0106 84.2184 21.9071C84.2184 24.5804 86.2229 26.697 88.8971 26.697C91.0137 26.697 92.3504 25.3604 92.7959 24.5804L91.236 23.466C90.6793 24.246 90.0105 24.8037 88.8971 24.8037C87.7828 24.8037 87.1149 24.3571 86.5573 23.3549L92.908 20.6816L92.6848 20.1249ZM86.2229 21.6839C86.2229 19.9017 87.6716 18.8994 88.6739 18.8994C89.4538 18.8994 90.2338 19.345 90.456 19.9017L86.2229 21.6839ZM80.9874 26.2514H83.104V12.3274H80.9874V26.2514ZM77.6454 18.1195C77.0897 17.5628 76.1976 17.0051 75.0832 17.0051C72.7443 17.0051 70.5166 19.1217 70.5166 21.796C70.5166 24.4693 72.6332 26.4747 75.0832 26.4747C76.1976 26.4747 77.0887 25.917 77.5342 25.3604H77.6454V26.0282C77.6454 27.8114 76.6431 28.8136 75.0832 28.8136C73.8587 28.8136 72.9666 27.9225 72.7443 27.1425L70.9622 27.9225C71.5188 29.148 72.8555 30.7069 75.1953 30.7069C77.6463 30.7069 79.6508 29.2591 79.6508 25.8049V17.3405H77.6463L77.6454 18.1195ZM75.1944 24.6915C73.7466 24.6915 72.5211 23.466 72.5211 21.796C72.5211 20.1249 73.7466 18.8984 75.1953 18.8984C76.6441 18.8984 77.7575 20.1249 77.7575 21.796C77.7575 23.466 76.6421 24.6915 75.1944 24.6915ZM102.376 12.3274H97.3635V26.2514H99.4791V21.016H102.376C104.716 21.016 106.943 19.345 106.943 16.6717C106.943 13.9974 104.716 12.3274 102.376 12.3274ZM102.487 19.0106H99.4791V14.2207H102.487C104.047 14.2207 104.938 15.5573 104.938 16.5596C104.827 17.7851 103.936 19.0106 102.487 19.0106ZM115.297 17.0061C113.739 17.0061 112.179 17.6739 111.622 19.1217L113.515 19.9017C113.961 19.1217 114.63 18.8994 115.41 18.8994C116.523 18.8994 117.526 19.5673 117.637 20.6816V20.7928C117.303 20.5705 116.412 20.2361 115.521 20.2361C113.515 20.2361 111.51 21.3494 111.51 23.3549C111.51 25.2492 113.181 26.4737 114.964 26.4737C116.412 26.4737 117.08 25.8059 117.637 25.1371H117.748V26.2514H119.754V20.9049C119.531 18.4539 117.637 17.0061 115.297 17.0061ZM115.075 24.6915C114.406 24.6915 113.404 24.3571 113.404 23.466C113.404 22.3527 114.63 22.0183 115.632 22.0183C116.523 22.0183 116.969 22.2415 117.526 22.4648C117.303 23.8004 116.189 24.6915 115.075 24.6915ZM126.772 17.3395L124.433 23.3549H124.321L121.871 17.3395H119.642L123.318 25.8059L121.202 30.4846H123.318L128.999 17.3395H126.772ZM108.057 26.2514H110.173V12.3274H108.057V26.2514Z"
                      fill="white"
                    />
                    <path
                      d="M0.562734 1.1875C0.228326 1.52191 0.117188 2.07859 0.117188 2.74741V27.3666C0.117188 28.0344 0.340457 28.5911 0.674864 28.9245L0.786003 29.0366L14.5989 15.2237V15.0004L0.562734 1.1875Z"
                      fill="url(#paint0_linear_2288_14397)"
                    />
                    <path
                      d="M19.051 19.9041L14.4844 15.3366V15.0021L19.051 10.4355L19.1631 10.5477L24.6208 13.6665C26.1807 14.5576 26.1807 16.0054 24.6208 16.8965L19.051 19.9041Z"
                      fill="url(#paint1_linear_2288_14397)"
                    />
                    <path
                      d="M19.1653 19.793L14.4876 15.1133L0.5625 29.0383C1.12018 29.595 1.90013 29.595 2.90236 29.1495L19.1663 19.792L19.1653 19.793Z"
                      fill="url(#paint2_linear_2288_14397)"
                    />
                    <path
                      d="M19.1653 10.435L2.90137 1.18868C1.89914 0.63199 1.12018 0.742137 0.5625 1.29981L14.4876 15.1127L19.1663 10.434L19.1653 10.435Z"
                      fill="url(#paint3_linear_2288_14397)"
                    />
                    <path
                      opacity="0.2"
                      d="M19.0483 19.6826L2.89455 28.8168C2.00346 29.3735 1.2235 29.2633 0.666818 28.8168L0.554688 28.9279L0.666818 29.0401C1.2235 29.4856 2.00346 29.5958 2.89455 29.0401L19.0483 19.6826Z"
                      fill="black"
                    />
                    <path
                      opacity="0.12"
                      d="M0.562734 28.8151C0.228326 28.4807 0.117188 27.924 0.117188 27.2562V27.3673C0.117188 28.0351 0.340457 28.5928 0.674864 28.9272V28.8151H0.562734ZM24.6242 16.562L19.0544 19.6809L19.1665 19.792L24.6242 16.6732C25.4042 16.2267 25.7386 15.671 25.7386 15.1133C25.7386 15.671 25.293 16.1155 24.6242 16.562Z"
                      fill="black"
                    />
                    <path
                      opacity="0.25"
                      d="M2.9026 1.30252L24.6242 13.6667C25.293 14.1132 25.7386 14.5588 25.7386 15.1154C25.7386 14.5588 25.4042 14.0011 24.6242 13.5555L2.9026 1.19138C1.3417 0.299293 0.117188 0.968108 0.117188 2.75029V2.86143C0.117188 1.19138 1.34269 0.410431 2.9026 1.30252Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <linearGradient
                      id="paint0_linear_2288_14397"
                      x1="13.2623"
                      y1="2.53505"
                      x2="-5.43378"
                      y2="21.2301"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#00BEFF" />
                      <stop offset="0.007" stopColor="#00BEFF" />
                      <stop offset="0.26" stopColor="#00BEFF" />
                      <stop offset="0.512" stopColor="#00E3FF" />
                      <stop offset="0.76" stopColor="#00E3FF" />
                      <stop offset="1" stopColor="#00E3FF" />
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_2288_14397"
                      x1="26.664"
                      y1="15.1153"
                      x2="-0.290094"
                      y2="15.1153"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#FFE000" />
                      <stop offset="0.409" stopColor="#FF9C00" />
                      <stop offset="0.775" stopColor="#FF9C00" />
                      <stop offset="1" stopColor="#FF9C00" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_2288_14397"
                      x1="16.6339"
                      y1="17.6715"
                      x2="-8.71756"
                      y2="43.0229"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#FF3A44" />
                      <stop offset="1" stopColor="#C31162" />
                    </linearGradient>
                    <linearGradient
                      id="paint3_linear_2288_14397"
                      x1="-2.89371"
                      y1="-6.97008"
                      x2="8.42753"
                      y2="4.35116"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#32A071" />
                      <stop offset="0.069" stopColor="#2DA771" />
                      <stop offset="0.476" stopColor="#15CF74" />
                      <stop offset="0.801" stopColor="#06E775" />
                      <stop offset="1" stopColor="#00F076" />
                    </linearGradient>
                    <clipPath id="clip0_2288_14397">
                      <rect
                        width="129"
                        height="31.7538"
                        fill="white"
                        transform="translate(0 -0.876953)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </a>
              <button
                className="ip-footer-desktop-btn"
                onClick={onInstallClick}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                Desktop version
              </button>
            </div>
          </div>

          <div className="ip-footer-divider" />
          <p className="ip-footer-copy">
            © 2026 Moonjar Technologies Private Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function DesktopInstallPage() {
  useFullWidth();
  const navigate = useNavigate();
  const platform = useMemo(() => detectPlatform(), []);
  const steps = PLATFORM_STEPS[platform];
  const videoFileName = PLATFORM_VIDEOS[platform] ?? "";
  const videoUrl = useS3VideoUrl(videoFileName);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    if (mq.matches) {
      navigate("/signup", { replace: true });
      return;
    }
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) navigate("/signup", { replace: true });
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [navigate]);

  const scrollToInstall = () => {
    document
      .getElementById("install-steps")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className={s.page}>
      <Navbar onInstallClick={scrollToInstall} />

      {/* Hero — "Maatra, now on desktop" (Figma node 4075:50083) */}
      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroHeading}>
            <span className={s.heroLine}>Maatra, now</span>
            <span className={s.heroLine}>
              on <span className={s.heroAccent}>desktop</span>
            </span>
          </div>
          <p className={s.heroSubtitle}>
            Install once and open from your dock whenever you need it.
          </p>
          <button className={s.heroCta} onClick={scrollToInstall}>
            See how to install
          </button>
        </div>
      </section>

      {/* Content — 5 document sections (Figma node 4075:50098) */}
      <section className={s.contentSection}>
        <div className={s.contentContainer}>
          <div className={s.contentBlock}>
            <h2 className={s.blockHeading}>Why you&apos;d use it</h2>
            <div className={s.blockBody}>
              <p>
                When your documents are already on your desktop. When your phone
                isn&apos;t in reach. When a family member finds the phone hard
                to use. Some things are just easier on desktop.
              </p>
            </div>
          </div>

          <div className={s.contentBlock}>
            <h2 className={s.blockHeading}>Your phone and desktop, together</h2>
            <div className={s.blockBody}>
              <p>
                Maatra works seamlessly across your desktop and your phone. Your
                records stay in sync, so whatever you do in one shows up in the
                other.
              </p>
            </div>
          </div>

          <div className={s.contentBlock}>
            <h2 className={s.blockHeading}>It works on its own</h2>
            <div className={s.blockBody}>
              <p>
                For day-to-day use, the desktop can be all you need. Add
                records, read documents, and keep up with the whole family —
                without reaching for your phone.
              </p>
              <p>New to Maatra? You can get started here too.</p>
            </div>
          </div>

          <div className={s.contentBlock} id="install-steps">
            <h2 className={s.blockHeading}>How to install</h2>
            <div className={s.blockBody}>
              <p>To keep Maatra in your dock:</p>
              <p>Then open it from your dock whenever you need it.</p>
            </div>
            <div className={s.stepRows}>
              {steps.map((step, i) => (
                <div key={i} className={s.stepRow}>
                  <span className={s.stepNum}>{i + 1}</span>
                  <span className={s.stepRowText}>{step}</span>
                </div>
              ))}
            </div>
            {videoUrl && (
              <div className={s.videoSection}>
                {!showVideo ? (
                  <button
                    className={s.showVideoBtn}
                    onClick={() => setShowVideo(true)}
                  >
                    Show video
                  </button>
                ) : (
                  <video
                    src={videoUrl}
                    controls
                    playsInline
                    autoPlay
                    className={s.videoPlayer}
                  />
                )}
              </div>
            )}
          </div>

          <div className={`${s.contentBlock} ${s.lastBlock}`}>
            <h2 className={s.blockHeading}>What stays on your phone</h2>
            <div className={s.blockBody}>
              <p>
                A few things are only on the mobile app: reminders and alerts,
                photographing a document, and subscribing. Reach for your phone
                for those.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback — "Tell us what you think" (Figma node 4075:50183) */}
      <section className={s.feedbackSection}>
        <div className={s.feedbackCard}>
          <h2 className={s.feedbackHeading}>Tell us what you think</h2>
          <p className={s.feedbackSub}>
            We&apos;re shaping the desktop experience around how people actually
            use it. Tell us what&apos;s working and what you&apos;d change.
          </p>
          <a href="mailto:support@maatra.health" className={s.feedbackBtn}>
            Share feedback
          </a>
        </div>
      </section>

      <Footer onInstallClick={scrollToInstall} />
    </div>
  );
}
